/**
 * A collection row is one NOA part (backend `NoticeOfArrival`), not one
 * `PreAlert` — a multi-part shipment has one row per part, so `awb` is no
 * longer unique across rows (docs/driver-collections-backend-spec.md §0.1,
 * §3, updated for the multiple-NOA-per-PreAlert plan). Always use `id` (the
 * part id) for React keys and cache lookups, never `awb`.
 */
export const COLLECTION_STATUSES = ['ReadyForCollection', 'Collected'] as const;
export type CollectionStatus = (typeof COLLECTION_STATUSES)[number];

export const COLLECTION_EVENT_TYPES = ['Collection', 'Correction'] as const;
export type CollectionEventType = (typeof COLLECTION_EVENT_TYPES)[number];

/** One row of `GET /api/v1/driver/collections` (spec 4.1) — one row per NOA part. */
export interface CollectionRow {
  /** The NOA part id, not the PreAlert id — see the header note above. */
  id: string;
  awb: string;
  customerName: string;
  /**
   * `GroundHandler.Name` via the AWB's 3-char prefix (backend spec §3, "exists").
   * Null only when the prefix has no `AwbPrefix` row — a left join here, since
   * the name is display-only, unlike the inner join that scopes the
   * GroundHandler role's own shipment list (ground-handler-shipments-backend.md §5).
   */
  handlerName: string | null;
  /** Null alongside `handlerName` when there is no ground-handler match — otherwise unresolved pending a schema decision (backend spec §3.4a). */
  pickupAddress: string | null;
  unit: string;
  /** This part's expected units. Null when the LLM couldn't read the part's pieces (and it isn't the shipment's only part) — render `—`, no deviation is flagged server-side. */
  totalUnits: number | null;
  /** The parent PreAlert id — groups parts of the same shipment together. */
  preAlertId: string;
  /** 1-based, in arrival order within the PreAlert. */
  partNumber: number;
  /** Number of NOA parts ingested so far for this PreAlert. */
  partCount: number;
  /** `PreAlert.NoOfParcels` — the AWB's total expected units, for the "60 of 101" style display. */
  awbTotalUnits: number;
  status: CollectionStatus;
  /** `PreAlert.NOADateTime` — when this AWB became collectable. */
  noaRegisteredAt: string | null;
  hasNoaDocument: boolean;
  /** Absolute deadline (Europe/Oslo end-of-day), null unless ReadyForCollection. Server-computed — never derive it client-side. */
  collectBy: string | null;
  isDelayed: boolean;
  /** `PreAlert.CustomsClearedAt` — retained and owned by another process, but rendered/filterable here too (§3.5). */
  customsClearedAt: string | null;
  collectedUnits: number | null;
  /** The first event's timestamp (spec 2.3) — does not move on a correction. */
  collectedAt: string | null;
  /** The first event's collector (spec 2.3) — does not move on a correction. */
  collectedBy: string | null;
  hasDeviation: boolean;
  eventCount: number;
  wasCorrected: boolean;
  originalCollectedUnits: number | null;
}

export interface CollectionsPage {
  items: CollectionRow[];
  skip: number;
  take: number;
  total: number;
}

/** One entry of `GET /api/v1/ground-handlers` — every active handler, not just those with rows in the current page (docs/driver-collections-pagination-backend.md §1). */
export interface GroundHandlerOption {
  id: string;
  name: string;
}

/** One entry of `GET /api/v1/driver/collections/{id}/events` (spec 4.5), oldest first. */
export interface CollectionEvent {
  id: string;
  type: CollectionEventType;
  collectedUnits: number;
  hasDeviation: boolean;
  collectedBy: string;
  organisationName: string;
  collectedAt: string;
  reason: string | null;
  supersededByEventId: string | null;
}
